import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, message, Select, DatePicker } from 'antd';
import { PlusOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import supplyChainService, { PurchaseOrder, Supplier, Warehouse } from '../../../services/supplyChainService';
import dayjs from 'dayjs';

const { Option } = Select;

const STATUS_MAP: Record<string, { label: string; color: string }> = {
    draft: { label: '草稿', color: 'default' },
    approval: { label: '待审批', color: 'orange' },
    ordered: { label: '已下单', color: 'blue' },
    production: { label: '生产中', color: 'cyan' },
    shipped: { label: '部分发货', color: 'purple' },
    received: { label: '全部到货', color: 'green' },
    closed: { label: '已关闭', color: 'default' },
    cancelled: { label: '已取消', color: 'red' },
};

const PurchaseOrderList: React.FC = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState<PurchaseOrder[]>([]);
    const [suppliers, setSuppliers] = useState<Supplier[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [ordersRes, suppliersRes, warehousesRes] = await Promise.all([
                supplyChainService.getPurchaseOrders({ status: statusFilter }),
                supplyChainService.getSuppliers({ page_size: 1000 }),
                supplyChainService.getWarehouses({ page_size: 1000 }),
            ]);
            const ordersData = ordersRes.data as any;
            const suppliersData = suppliersRes.data as any;
            const warehousesData = warehousesRes.data as any;
            setOrders(Array.isArray(ordersData) ? ordersData : ordersData?.results || []);
            setSuppliers(Array.isArray(suppliersData) ? suppliersData : suppliersData?.results || []);
            setWarehouses(Array.isArray(warehousesData) ? warehousesData : warehousesData?.results || []);
        } catch (error) {
            message.error('加载数据失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [statusFilter]);

    const handleCreate = () => {
        navigate('/supply-chain/procurement/orders/new');
    };

    const handleView = (id: number) => {
        navigate(`/supply-chain/procurement/orders/${id}`);
    };

    const columns = [
        {
            title: '采购单号',
            dataIndex: 'po_number',
            key: 'po_number',
            width: 150,
            render: (text: string, record: PurchaseOrder) => (
                <Button type="link" onClick={() => handleView(record.id!)}>
                    {text}
                </Button>
            ),
        },
        {
            title: '供应商',
            dataIndex: 'supplier_name',
            key: 'supplier_name',
            width: 150,
        },
        {
            title: '供应商代码',
            dataIndex: 'supplier_code',
            key: 'supplier_code',
            width: 120,
        },
        {
            title: '目标仓库',
            dataIndex: 'target_warehouse_name',
            key: 'target_warehouse_name',
            width: 120,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => {
                const config = STATUS_MAP[status] || { label: status, color: 'default' };
                return <Tag color={config.color}>{config.label}</Tag>;
            },
        },
        {
            title: '下单日期',
            dataIndex: 'order_date',
            key: 'order_date',
            width: 120,
            render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '预计到货',
            dataIndex: 'expected_arrival_date',
            key: 'expected_arrival_date',
            width: 120,
            render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD') : '-',
        },
        {
            title: '总金额',
            dataIndex: 'total_amount',
            key: 'total_amount',
            width: 120,
            render: (amount: number) => `¥${Number(amount || 0).toFixed(2)}`,
        },
        {
            title: '创建时间',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 150,
            render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-',
        },
        {
            title: '操作',
            key: 'actions',
            width: 100,
            render: (_: any, record: PurchaseOrder) => (
                <Button type="link" icon={<EyeOutlined />} onClick={() => handleView(record.id!)}>
                    查看
                </Button>
            ),
        },
    ];

    return (
        <Card
            title="采购订单"
            extra={
                <Space>
                    <Select
                        placeholder="筛选状态"
                        allowClear
                        style={{ width: 120 }}
                        value={statusFilter}
                        onChange={setStatusFilter}
                    >
                        {Object.entries(STATUS_MAP).map(([key, { label }]) => (
                            <Option key={key} value={key}>{label}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                        新建采购单
                    </Button>
                </Space>
            }
        >
            <Table
                dataSource={orders}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 20 }}
                scroll={{ x: 1200 }}
            />
        </Card>
    );
};

export default PurchaseOrderList;
